import { FileCopyOutlined } from "@mui/icons-material";
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import TransferList from "App/Components/TransferList/TransferList";
import { IFeature } from "App/Typings/AbacFeature";
import useFeatureDetailsState from "../State/FeatureDetailsState";
import { ManageFeatureVariations } from "./ManageFeatureVariations";

interface IFeatureDetailsFromProps {
  feature: IFeature;
}

export function FeatureDetailsForm({ feature }: IFeatureDetailsFromProps) {
  const {
    getters: { isActive, formik, rightTransferPanel, leftTransferPanel },
    setters: { setIsActive },
  } = useFeatureDetailsState(feature);

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        {!isActive && (
          <Alert sx={{ mb: { xs: "8%", sm: "1%" } }} severity="error">
            This Feature is currently deactivated. The Feature will now appear in its inaccessible state to
            users, even if they have the required Attributes to access this Feature.
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              sx={{ mt: "2%" }}
              name="featureName"
              label="Feature Name"
              placeholder="Enter Feature Name"
              value={formik.values.featureName}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.featureName)}
              helperText={formik.errors.featureName || `${formik.values.featureName.trim().length}/100`}
              required
            />
          </Grid>
          {feature.key !== null && (
            <Grid item xs={12}>
              <TextField
                sx={{ mb: 1, minWidth: "28ch" }}
                name="featureKey"
                label="Feature Key (Generated Automatically)"
                placeholder="Enter Feature Name"
                value={feature.key}
                variant="standard"
                size="small"
                InputProps={{
                  sx: { fontSize: "0.8rem", px: 1 },
                  endAdornment: (
                    <Tooltip title="Copy to Clipboard">
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(feature.key!);
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      >
                        <FileCopyOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
                disabled
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={5}
              name="featureDescription"
              label="Description"
              placeholder="Enter Description"
              value={formik.values.featureDescription}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.featureDescription)}
              helperText={
                formik.errors.featureDescription || `${formik.values.featureDescription.trim().length}/500`
              }
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              label="Require All Attributes To Access Feature?"
              sx={{ mb: "2%" }}
              control={
                <Checkbox
                  name="requireAll"
                  color="primary"
                  checked={formik.values.requireAll}
                  onChange={formik.handleChange}
                />
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TransferList leftPanel={leftTransferPanel} rightPanel={rightTransferPanel} />
          </Grid>

          <Grid item xs={12}>
            <Tooltip
              title="Feature variations allow you to create different versions of your feature and choose a distribution ratio for the version presented to users"
              placement="right"
              arrow
            >
              <FormControlLabel
                label="Has Variations?"
                control={
                  <Checkbox
                    name="hasVariations"
                    color="primary"
                    checked={formik.values.hasVariations}
                    onChange={formik.handleChange}
                  />
                }
              />
            </Tooltip>
          </Grid>

          {formik.values.hasVariations && (
            <Grid item xs={12}>
              <ManageFeatureVariations
                featureId={feature.id}
                setVariations={(v) => {
                  formik.setFieldValue("variationAttributes", v);
                }}
                fieldErrorMessage={formik.errors}
              />
            </Grid>
          )}

          <Grid container item xs={6}>
            <Button
              color={isActive ? "secondary" : "primary"}
              sx={{ width: { xs: "100%", sm: "50%" } }}
              variant="contained"
              onClick={() => setIsActive(!isActive)}
            >
              {isActive ? "Deactivate Feature" : "Activate Feature"}
            </Button>
          </Grid>
          <Grid container item justifyContent="flex-end" xs={6}>
            <Button
              sx={{ width: { xs: "100%", sm: "30%" } }}
              variant="contained"
              type="submit"
              disabled={!formik.isValid}
            >
              Save Changes
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
